import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return {
    items,
    pagination,
    totalCount,
    category_count: payload?.category_count,
    payload,
  };
};

// --- Interior designs ---
export const useCorporateInteriorDesign = () => {
  const getCorporateInteriorDesign = async (
    setCorporateInteriorDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
  ) => {
    try {
      const params = {};
      if (searchFilter) {
        params.searchFilter = searchFilter;
      }
      const response = await Apis.getCorporateInteriorDesign(offset, limit, params);
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      setCorporateInteriorDesignData(items);
      setTotal(totalCount);
      return { data: items, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching corporate interior design:", error);
      throw new Error(getApiError(error, "Failed to fetch corporate interior designs."));
    }
  };

  const createCorporateInteriorDesign = async (data) => {
    try {
      const response = await Apis.createCorporateInteriorDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating corporate interior design:", error);
      throw error;
    }
  };

  const updateCorporateInteriorDesign = async (id, data) => {
    try {
      const response = await Apis.updateCorporateInteriorDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate interior design:", error);
      throw error;
    }
  };

  const deleteCorporateInteriorDesign = async (id) => {
    try {
      const response = await Apis.deleteCorporateInteriorDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting corporate interior design:", error);
      throw error;
    }
  };

  const getCorporateInteriorDesignById = async (id, setInteriorData) => {
    try {
      const response = await Apis.getCorporateInteriorDesignById(id);
      const design = unwrapResponseBody(response?.data);
      if (typeof setInteriorData === "function") setInteriorData(design);
      return design;
    } catch (error) {
      console.error("Error fetching corporate interior design by id:", error);
      throw error;
    }
  };

  const showCorporateInteriorDesignOnHomeScreen = async (corporateInteriorDesignId, homeScreen) => {
    try {
      const data = {
        corporateInteriorDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addCorporateInteriorDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateCorporateInteriorDesignPosition = async (corporateInteriorDesignId, newPosition) => {
    try {
      const response = await Apis.updateCorporateInteriorDesignPosition({
        corporateInteriorDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate interior design position:", error);
      throw error;
    }
  };

  return {
    getCorporateInteriorDesign,
    createCorporateInteriorDesign,
    updateCorporateInteriorDesign,
    deleteCorporateInteriorDesign,
    getCorporateInteriorDesignById,
    showCorporateInteriorDesignOnHomeScreen,
    updateCorporateInteriorDesignPosition,
  };
};
